import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import ServiceStatusMonitor from '../components/ServiceStatusMonitor';
import PerformanceMonitor from '../components/PerformanceMonitor';
import { optimizedApiService } from '../lib/apiOptimized';
import { errorHandler } from '../lib/errorHandler';
import { Package, Upload, Search, DollarSign, Clock, Activity, Settings } from 'lucide-react';

// Type definitions for API responses
interface Shipment {
  id: number;
  status: string;
  shipping_cost?: number;
  display_shipping_cost?: number;
  show_rate?: boolean;
  currency?: string;
  shipment_tracking_number?: string;
  service_type: string;
  created_at: string;
  quantity: number;
  to_address?: {
    name: string;
    city: string;
    state_province: string;
  };
}

interface ShipmentsResponse {
  count: number;
  results: Shipment[];
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [showPerformanceMonitor, setShowPerformanceMonitor] = useState(false);

  // Fetch real data using optimized API (ordered by newest first)
  const { data: shipmentsData, isLoading: shipmentsLoading, error } = useQuery<ShipmentsResponse>({
    queryKey: ['shipments', 'dashboard'],
    queryFn: () => optimizedApiService.shipments.getShipments({ page_size: 100, page: 1, ordering: '-created_at' }) as Promise<ShipmentsResponse>,
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      errorHandler.handleApiError(error as any, 'Dashboard Shipments');
    }
  }, [error]);

  // Calculate stats from real data
  const totalShipments = shipmentsData?.count || 0;
  const recentShipments = shipmentsData?.results?.slice(0, 5) || [];
  
  // Calculate total shipping cost from shipments (use display cost if available and user has permission)
  const totalShippingCost = shipmentsData?.results?.reduce((sum: number, shipment: Shipment) => {
    if (shipment.show_rate && shipment.display_shipping_cost) {
      return sum + shipment.display_shipping_cost;
    }
    // Don't include cost if user doesn't have permission to see rates
    return sum;
  }, 0) || 0;

  // Calculate pending shipments
  const pendingShipments = shipmentsData?.results?.filter((shipment: Shipment) => 
    shipment.status === 'created' || shipment.status === 'pending'
  ).length || 0;

  const stats = [
    {
      title: 'Total Shipments',
      value: totalShipments.toLocaleString(),
      change: '+12%', // This would need historical data to calculate
      changeType: 'positive' as const,
      icon: Package,
      description: 'Shipments created',
    },
    {
      title: 'Pending Shipments',
      value: pendingShipments.toString(),
      change: '-5%', // This would need historical data to calculate
      changeType: 'negative' as const,
      icon: Clock,
      description: 'Awaiting pickup',
    },
    // Only show shipping cost if user has permission
    ...(shipmentsData?.results?.some(s => s.show_rate) ? [{
      title: 'Total Shipping Cost',
      value: `$${totalShippingCost.toFixed(2)}`,
      change: '+8%', // This would need historical data to calculate
      changeType: 'positive' as const,
      icon: DollarSign,
      description: 'Total cost',
    }] : []),
  ];

  const quickActions = [
    {
      title: 'Create Label',
      description: 'Create a new shipping label',
      icon: Package,
      link: '/labels/create',
      color: 'bg-primary-500',
    },
    {
      title: 'Bulk Upload',
      description: 'Upload multiple labels at once',
      icon: Upload,
      link: '/bulk',
      color: 'bg-success-500',
    },
    {
      title: 'Track Shipment',
      description: 'Track your shipments',
      icon: Search,
      link: '/tracking',
      color: 'bg-warning-500',
    },
    {
      title: 'Get Rates',
      description: 'Compare shipping rates',
      icon: DollarSign,
      link: '/rates',
      color: 'bg-error-500',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Service Status Monitor */}
      <ServiceStatusMonitor />

      {/* Performance Monitor Toggle */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowPerformanceMonitor(!showPerformanceMonitor)}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <Activity className="h-4 w-4" />
          <span>{showPerformanceMonitor ? 'Hide' : 'Show'} Performance</span>
        </button>
      </div>

      {/* Performance Monitor */}
      {showPerformanceMonitor && (
        <PerformanceMonitor />
      )}

      {/* Welcome Section */}
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
        <div className="flex items-center space-x-4 mb-4">
          <img 
            src="https://www.gxpresss.com/images/gxp-logo-white.png" 
            alt="GXPress Logo" 
            className="h-10 w-auto"
          />
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">
              Welcome back, {user?.first_name || user?.username}!
            </h1>
            <p className="text-secondary-600">
              Here's what's happening with your shipments today.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600">
                      {stat.title}
                    </p>
                    <p className="text-2xl font-bold text-secondary-900">
                      {stat.value}
                    </p>
                    <p className="text-xs text-secondary-500">
                      {stat.description}
                    </p>
                  </div>
                  <div className="p-3 bg-primary-100 rounded-full">
                    <Icon className="h-6 w-6 text-primary-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  <span
                    className={`text-sm font-medium ${
                      stat.changeType === 'positive'
                        ? 'text-success-600'
                        : 'text-error-600'
                    }`}
                  >
                    {stat.change}
                  </span>
                  <span className="text-sm text-secondary-500 ml-2">
                    from last month
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 mb-6">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Link key={index} to={action.link}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-full ${action.color}`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-secondary-900">
                          {action.title}
                        </h3>
                        <p className="text-sm text-secondary-600">
                          {action.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 mb-6">
          Recent Activity
        </h2>
        <Card>
          <CardHeader>
            <CardTitle>Latest Shipments</CardTitle>
            <CardDescription>
              Your most recent shipments
            </CardDescription>
          </CardHeader>
          <CardContent>
            {shipmentsLoading ? (
              <div className="text-center py-8">
                <p className="text-secondary-600">Loading shipments...</p>
              </div>
            ) : recentShipments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-secondary-600">No shipments found. Create your first shipment!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentShipments.map((shipment: Shipment) => (
                  <div
                    key={shipment.id}
                    className="flex items-center justify-between p-4 border border-secondary-200 rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-2 bg-primary-100 rounded-full">
                        <Package className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-secondary-900">
                          Shipment #{shipment.shipment_tracking_number || shipment.id}
                        </p>
                        <p className="text-sm text-secondary-600">
                          {shipment.to_address?.city}, {shipment.to_address?.state_province}
                        </p>
                        <p className="text-xs text-secondary-500">
                          {shipment.quantity} package{shipment.quantity > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {shipment.show_rate && shipment.display_shipping_cost ? (
                        <p className="text-sm font-medium text-secondary-900">
                          ${shipment.display_shipping_cost.toFixed(2)} {shipment.currency || 'USD'}
                        </p>
                      ) : null}
                      <p className="text-sm text-secondary-600">
                        {shipment.service_type || 'Ground'}
                      </p>
                      <p className="text-xs text-secondary-500 capitalize">
                        {shipment.status}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 text-center">
              <Link
                to="/labels"
                className="text-primary-600 hover:text-primary-500 font-medium"
              >
                View all shipments →
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
